import { chunk } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import * as Styled from './styled';
import { AboutSectionWrapper } from '../about/styled';
import { useLocale } from 'locale';
import { ShowMore } from 'screens/ShortlistPage/styled';
import ChevronDown from 'assets/svg/chevron-down.svg';
import ChevronUp from 'assets/svg/chevron-up.svg';
import { CenteredCarouselControls } from 'ds/components/carousels/styled';
import Carousel from 'ds/components/carousels/carousel';
import { ScrollBox } from 'ds/components/carousels/scroll-box';
import { Badge } from 'screens/AgentOfficePages/common/components/Badge';
import { BADGE_SIZE } from 'screens/AgentOfficePages/common/components/Badge/styled';
import { BreakpointValue, useScreenBreakpoint } from 'consts/breakpoints';
import { IOfficeAward } from 'utils/entities';


const COLUMN_PER_PAGE = 5;
const COUNT_PER_COLUMN = 1;
const ANIMATION_SEED_PER_SECOND = 50;

export enum AwardsMode {
  Expand = 'expand',
  Carousel = 'carousel',
  Scroll = 'scroll',
  Animate = 'aminate',
}

export const Awards: React.FC<{ awards: IOfficeAward[]; mode?: AwardsMode; withGrayBg?: boolean  }> = ({ awards, mode, withGrayBg }) => {
  const screen = useScreenBreakpoint();
  const { t } = useLocale();
  const [ isOpen, setIsOpen ] = useState(false);
  const itemsWidths = useRef<number[]>([]);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);

  const pages = useMemo(() => chunk(chunk(awards, COUNT_PER_COLUMN), COLUMN_PER_PAGE), [ awards ]);
  const itemsTotalWidth =
    itemsWidths &&
    itemsWidths.current &&
    itemsWidths.current.length &&
    itemsWidths.current.reduce((totalWidth, itemWidth) => (totalWidth += itemWidth), 0);

  const countCarouselNextClickLimit = () => {
    if (
      !carouselWrapperRef ||
      !carouselWrapperRef.current ||
      !itemsWidths ||
      !itemsWidths.current ||
      !itemsWidths.current.length
    ) {
      return pages.length;
    }

    if (carouselWrapperRef.current.clientWidth > itemsTotalWidth) return 0;

    let scrolledWidth = 0;
    let clickLimit = 1;
    itemsWidths.current.forEach(width => {
      scrolledWidth += width;
      if (itemsTotalWidth - scrolledWidth >= carouselWrapperRef.current.clientWidth) clickLimit++;
    });

    return clickLimit;
  };

  const widths: number[] = [];
  const awardCards = awards.map((badge, i) => (<Badge {...badge} key={i} />));
  const animationTime = Math.ceil((awardCards.length * BADGE_SIZE.width) / ANIMATION_SEED_PER_SECOND);

  const getContentByMode = (m: AwardsMode) => {
    switch (m) {
      case AwardsMode.Animate:
        return (
          <Styled.BadgeSizeSpace>
            <Styled.InfiniteAnimationScrollSection>
              <Styled.ScrollSectionFirst animationTime={animationTime}>{awardCards}</Styled.ScrollSectionFirst>
              <Styled.ScrollSectionLast animationTime={animationTime}>{awardCards}</Styled.ScrollSectionLast>
            </Styled.InfiniteAnimationScrollSection>
          </Styled.BadgeSizeSpace>
        );
      case AwardsMode.Carousel:
        return (
          <Styled.AwardsWrapper>
            <Styled.CarouselWrapper ref={carouselWrapperRef}>
              <CenteredCarouselControls>
                <Carousel
                  hasArrows
                  hasArrowsWhiteBg
                  nextClickLimit={countCarouselNextClickLimit()}
                >
                  {pages.map((page, pIdx) => (
                    <Styled.Page
                      key={pIdx}
                      ref={(ref) => {
                        if (ref && ref.offsetWidth) widths.push(ref.offsetWidth);
                        if (pages.length === pIdx + 1) itemsWidths.current = widths;
                      }}
                    >
                      {page.map((column, cIdx) => (<Styled.Column key={cIdx}>{column.map((badge, bIdx) => (<Badge {...badge} key={bIdx} />))}</Styled.Column>))}
                    </Styled.Page>
                  ))}
                </Carousel>
              </CenteredCarouselControls>
            </Styled.CarouselWrapper>
          </Styled.AwardsWrapper>
        );

      case AwardsMode.Expand:
        return (
          <>
            <Styled.MobileTitle weight="bold">{t('awards.section.mobile.title')}</Styled.MobileTitle>
            <Styled.Column>{awardCards.slice(0, isOpen ? undefined : COUNT_PER_COLUMN)}</Styled.Column>
            {awardCards.length > COUNT_PER_COLUMN ? (
              <Styled.ShowMoreWrapper>
                <ShowMore onClick={() => setIsOpen(!isOpen)}>
                  {isOpen ? (
                    <>{t('collapsable.showLess')}<ChevronUp /></>
                  ) : (
                    <>{t('collapsable.showMore')}<ChevronDown /></>
                  )}
                </ShowMore>
              </Styled.ShowMoreWrapper>
            ) : null}
          </>
        );
      case AwardsMode.Scroll:
        return (
          <Styled.ScrollBoxWrapper>
            <ScrollBox
              cacheKey={`area-highlights`}
              autoWidth
            >
              {awardCards}
            </ScrollBox>
          </Styled.ScrollBoxWrapper>
        );
      default:
        return null;
    }
  };


  const getContent = (size: BreakpointValue) => {
    if (mode) return getContentByMode(mode);
    switch (true) {
      case size === 1:
        return getContentByMode(AwardsMode.Expand);
      case size === 2:
        return getContentByMode(AwardsMode.Scroll);
      case size >= 3:
        return getContentByMode(awardCards.length > 5 ? AwardsMode.Animate : AwardsMode.Scroll);
      default:
        return null;
    }
  };


  return (
    <AboutSectionWrapper noShrink>{getContent(screen)}</AboutSectionWrapper>
  );
};
